<template>
  <div class="message-row" :class="{ owner: !message.isSystem }">
    <AgentChatAvatar v-if="message.isSystem" />
    <div class="message-body">
      <ChatBubble :direction="message.isSystem ? 'incoming' : 'outgoing'">
        <template v-for="(segment, index) in segments" :key="index">
          <RouterLink v-if="segment.kind === 'route'" v-slot="{ href, navigate }" :to="segment.path" custom>
            <a class="message-link" :href="href" @click="followInAppLink($event, navigate)">{{ segment.text }}</a>
          </RouterLink>
          <a
            v-else-if="segment.kind === 'url'"
            class="message-link"
            :href="segment.url"
            target="_blank"
            rel="noopener noreferrer"
          >
            {{ segment.text }}
          </a>
          <template v-else>{{ segment.text }}</template>
        </template>
      </ChatBubble>
      <div class="meta">{{ time }}</div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { RouterLink } from 'vue-router';

import { ChatBubble } from '@tallo/design-system';

import { AgentChatMessageDto } from '../models/agent-chat.interfaces';
import { useAgentChatStore } from '../stores/agent-chat.store';
import { splitMessageLinks } from '../utils/message-links.utils';
import { formatMessageTime } from '../utils/thread-format.utils';
import AgentChatAvatar from './agent-chat-avatar.vue';

const props = defineProps<{ message: AgentChatMessageDto }>();

const store = useAgentChatStore();

const segments = computed(() => splitMessageLinks(props.message.content));
const time = computed(() => formatMessageTime(props.message.createdAt));

/**
 * The page an in-app link opens lands behind the drawer, so following one closes the panel.
 * navigate() leaves modified clicks (cmd/ctrl for a new tab) to the browser without preventing
 * them, which is how those keep the panel open.
 */
function followInAppLink(event: MouseEvent, navigate: (event: MouseEvent) => unknown): void {
  navigate(event);

  if (event.defaultPrevented) {
    store.closePanel();
  }
}
</script>

<style scoped lang="scss">
.message-row {
  display: flex;
  gap: 0.5625rem;

  .agent-avatar {
    margin-top: 2px;
  }

  .message-body {
    max-width: 85%;
  }

  .message-link {
    color: inherit;
    text-decoration: underline;
    text-underline-offset: 2px;
  }

  .meta {
    padding: 3px 4px 0;
    font-size: 0.6875rem;
    color: var(--t-color-neutral-400);
  }

  &.owner {
    flex-direction: row-reverse;

    .meta {
      text-align: right;
    }
  }
}
</style>
